/**
 * AccessibleChart — wraps a Recharts (or any) chart with WCAG 2.1 features:
 *
 *  • Adds an SVG <title>/<desc> equivalent via aria-label + aria-describedby
 *    so screen readers announce a meaningful name and summary instead of
 *    "graphic".  (WCAG 1.1.1 — Non-text Content, 4.1.2 — Name, Role, Value)
 *  • Optionally renders a "View as table" toggle that switches the chart for
 *    a fully accessible <table> rendering of the same data — required for
 *    full conformance on chart-only data displays. (WCAG 1.3.1 — Info and
 *    Relationships, plus the EN 301 549 SVG accessibility profile.)
 *  • Respects the user's `prefers-reduced-motion` setting and propagates it
 *    to its children via the `disableAnimation` prop so Recharts disables
 *    its built-in entrance animation.
 *
 * Usage:
 *
 *    <AccessibleChart
 *       title="Budget utilisation"
 *       description="Pie chart showing spent vs remaining budget for the project."
 *       tableData={[{ name: 'Spent', value: 12000 }, { name: 'Remaining', value: 68000 }]}
 *       tableColumns={[
 *         { key: 'name',  label: 'Segment' },
 *         { key: 'value', label: 'EUR', format: v => v.toLocaleString() }
 *       ]}
 *    >
 *      <ResponsiveContainer …>…</ResponsiveContainer>
 *    </AccessibleChart>
 */
import React, { useEffect, useId, useState } from 'react';
import { Button } from './ui/button';
import { Table, BarChart3 } from 'lucide-react';

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    try {
      return typeof window !== 'undefined'
        && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch { return false; }
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => setReduced(mq.matches);
    try { mq.addEventListener('change', handler); }
    catch { mq.addListener(handler); /* legacy Safari */ }
    return () => {
      try { mq.removeEventListener('change', handler); }
      catch { mq.removeListener(handler); }
    };
  }, []);
  return reduced;
}

export const AccessibleChart = ({
  title,
  description,
  tableData,
  tableColumns,
  children,
  className = '',
  hideToggle = false,
  defaultView = 'chart',
}) => {
  const id = useId();
  const [view, setView] = useState(defaultView);
  const reducedMotion = usePrefersReducedMotion();

  const titleId = `chart-title-${id}`;
  const descId  = `chart-desc-${id}`;
  const hasTable = Array.isArray(tableData) && tableData.length > 0 && Array.isArray(tableColumns);

  // Augment Recharts children with a `isAnimationActive={false}` prop when
  // the user prefers reduced motion. We try to do this transparently by
  // cloning the first child if it accepts the prop; otherwise we just rely
  // on the CSS rule in App.css that neutralises transitions.
  const renderChildren = () => {
    if (!reducedMotion) return children;
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;
      return React.cloneElement(child, { isAnimationActive: false });
    });
  };

  return (
    <figure
      className={className}
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descId : undefined}
      role="group"
    >
      {(title || (hasTable && !hideToggle)) && (
        <figcaption className="flex items-center justify-between gap-2 mb-2">
          {title ? (
            <span id={titleId} className="text-sm font-semibold text-slate-700">
              {title}
            </span>
          ) : <span />}
          {hasTable && !hideToggle && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={() => setView((v) => (v === 'chart' ? 'table' : 'chart'))}
              data-testid={`chart-view-toggle-${id}`}
              aria-pressed={view === 'table'}
              aria-label={view === 'chart' ? 'Show data as a table' : 'Show data as a chart'}
            >
              {view === 'chart'
                ? <><Table className="h-3.5 w-3.5 mr-1" aria-hidden="true" />View as table</>
                : <><BarChart3 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />View as chart</>
              }
            </Button>
          )}
        </figcaption>
      )}

      {description && (
        <p id={descId} className="sr-only">{description}</p>
      )}

      {view === 'chart' || !hasTable ? (
        renderChildren()
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-md">
          <table className="w-full text-sm" role="table">
            <caption className="sr-only">{title || 'Chart data'}</caption>
            <thead className="bg-slate-50">
              <tr>
                {tableColumns.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200"
                  >
                    {c.label || c.key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  {tableColumns.map((c) => {
                    const raw = row[c.key];
                    const display = c.format ? c.format(raw, row) : (raw ?? '—');
                    return (
                      <td
                        key={c.key}
                        className="px-3 py-2 border-b border-slate-100 text-slate-700"
                      >
                        {display}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </figure>
  );
};

export default AccessibleChart;
